import { useState, useEffect, useRef, useCallback } from "react";
import type {
  TimelineEvent,
  TimelineEra,
  TimelinePerson,
  TimelineScene,
} from "@/hooks/useTimelineData";

type SearchResult =
  | { type: "event"; item: TimelineEvent; year: number }
  | { type: "era"; item: TimelineEra; startYear: number; endYear: number }
  | { type: "person"; item: TimelinePerson; birthYear: number; deathYear: number | null }
  | { type: "scene"; item: TimelineScene; year: number | null };

type Props = {
  events: TimelineEvent[];
  eras: TimelineEra[];
  people: TimelinePerson[];
  scenes: TimelineScene[];
  onSelect: (result: SearchResult) => void;
};

function parseYear(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return new Date(dateStr).getFullYear();
}

function fuzzyMatch(text: string, query: string): boolean {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Check if query words are in text
  const queryWords = lowerQuery.split(/\s+/).filter(Boolean);
  return queryWords.every(word => lowerText.includes(word));
}

export function TimelineSearch({ events, eras, people, scenes, onSelect }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Search results
  const results: SearchResult[] = [];

  if (query.trim().length > 0) {
    // Search events
    for (const event of events) {
      if (fuzzyMatch(event.title, query) || (event.description && fuzzyMatch(event.description, query))) {
        const year = parseYear(event.date);
        if (year !== null) {
          results.push({ type: "event", item: event, year });
        }
      }
    }

    // Search eras
    for (const era of eras) {
      if (fuzzyMatch(era.title, query) || (era.description && fuzzyMatch(era.description, query))) {
        const startYear = parseYear(era.startDate);
        const endYear = parseYear(era.endDate);
        if (startYear !== null && endYear !== null) {
          results.push({ type: "era", item: era, startYear, endYear });
        }
      }
    }

    // Search people
    for (const person of people) {
      if (fuzzyMatch(person.name, query) || (person.bio && fuzzyMatch(person.bio, query))) {
        const birthYear = parseYear(person.birthDate);
        const deathYear = parseYear(person.deathDate);
        if (birthYear !== null) {
          results.push({ type: "person", item: person, birthYear, deathYear });
        }
      }
    }

    // Search scenes
    for (const scene of scenes) {
      if (
        fuzzyMatch(scene.title, query) ||
        fuzzyMatch(scene.sceneCode, query)
      ) {
        const year = parseYear(scene.computedDate);
        results.push({ type: "scene", item: scene, year });
      }
    }
  }

  // Limit results
  const limitedResults = results.slice(0, 12);

  // Keyboard shortcut (Cmd+K)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
        setQuery("");
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, limitedResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && limitedResults[selectedIndex]) {
        e.preventDefault();
        onSelect(limitedResults[selectedIndex]);
        setIsOpen(false);
        setQuery("");
      }
    },
    [limitedResults, selectedIndex, onSelect]
  );

  const handleSelect = useCallback(
    (result: SearchResult) => {
      onSelect(result);
      setIsOpen(false);
      setQuery("");
    },
    [onSelect]
  );

  const getTypeIcon = (type: "event" | "era" | "person" | "scene") => {
    switch (type) {
      case "event":
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3" strokeWidth={2} />
          </svg>
        );
      case "era":
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeWidth={2} d="M4 12h16" />
          </svg>
        );
      case "person":
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case "scene":
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  const getResultLabel = (result: SearchResult) => {
    switch (result.type) {
      case "event":
        return result.item.title;
      case "era":
        return result.item.title;
      case "person":
        return result.item.name;
      case "scene":
        return result.item.title || result.item.sceneCode || "Untitled Scene";
    }
  };

  const getResultDate = (result: SearchResult) => {
    switch (result.type) {
      case "event":
        return String(result.year);
      case "era":
        return `${result.startYear}–${result.endYear}`;
      case "person":
        return `${result.birthYear}–${result.deathYear ?? "present"}`;
      case "scene":
        return result.year ? String(result.year) : "Undated";
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search trigger button */}
      <button
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="flex items-center gap-2 px-3 py-1.5 bg-surface-elevated hover:bg-border text-muted text-xs rounded border border-border transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span>Search</span>
        <span className="text-muted-foreground ml-1">⌘K</span>
      </button>

      {/* Search dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-surface border border-border rounded-lg shadow-card overflow-hidden z-50">
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-2 px-2">
              <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search events, eras, people, scenes..."
                className="flex-1 bg-transparent text-sm text-foreground placeholder-muted-foreground focus:outline-none"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="text-muted hover:text-foreground"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto">
            {query.trim().length === 0 ? (
              <div className="p-4 text-center text-muted text-xs">
                Type to search events, eras, people, and scenes
              </div>
            ) : limitedResults.length === 0 ? (
              <div className="p-4 text-center text-muted text-xs">
                No results for "{query}"
              </div>
            ) : (
              <div className="py-1">
                {limitedResults.map((result, index) => (
                  <button
                    key={`${result.type}-${result.item.id}`}
                    onClick={() => handleSelect(result)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                      index === selectedIndex
                        ? "bg-primary-500/10 text-primary-400"
                        : "hover:bg-surface-elevated text-foreground"
                    }`}
                  >
                    <span className={index === selectedIndex ? "text-primary-400" : "text-muted"}>
                      {getTypeIcon(result.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{getResultLabel(result)}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {result.type.charAt(0).toUpperCase() + result.type.slice(1)} • {getResultDate(result)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground flex items-center justify-between">
            <span>↑↓ to navigate</span>
            <span>↵ to select</span>
            <span>esc to close</span>
          </div>
        </div>
      )}
    </div>
  );
}
